import { ReactElement, useEffect } from "react";

import {
  FormField,
  IconButton,
  Select,
  Row,
  RefreshIcon,
  Combobox,
} from "@hightouch/htui";
import * as Yup from "yup";

import {
  useMicrosoftexcelListSheetsQuery,
  useMicrosoftexcelListWorkbooksQuery,
} from "src/graphql";

import {
  CustomQuery,
  CustomQueryFormProps,
  CustomQueryViewProps,
} from "./custom-query";

function QueryForm({
  source,
  query,
  onChange,
}: Readonly<CustomQueryFormProps>): ReactElement<any, any> {
  if (query?.type && query?.type !== "excel") {
    return <>{`Query type ${query?.type} not valid for Excel source`}</>;
  }

  // TODO: Fix this hook issue.
  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  // biome-ignore lint/correctness/useHookAtTopLevel: legacy suppression
  useEffect(() => {
    if (!query?.type) {
      onChange({ ...query, type: "excel" });
    }
  }, [query]);

  const {
    data: workbooksData,
    isLoading: workbooksLoading,
    refetch: refetchWorkbooks,
    error: workbooksError,
    // TODO: Fix this hook issue.
    // biome-ignore lint/correctness/useHookAtTopLevel: legacy suppression
  } = useMicrosoftexcelListWorkbooksQuery({
    connectionId: String(source.id),
  });
  const workbooks = workbooksData?.microsoftexcelListWorkbooks || [];

  const {
    data: sheetsData,
    isLoading: sheetsLoading,
    refetch: refetchSheets,
    error: sheetsError,
    // TODO: Fix this hook issue.
    // biome-ignore lint/correctness/useHookAtTopLevel: legacy suppression
  } = useMicrosoftexcelListSheetsQuery(
    {
      connectionId: String(source.id),
      workbookId: String(query?.workbookId),
    },
    { enabled: Boolean(query?.workbookId) },
  );
  const sheets = sheetsData?.microsoftexcelListSheets || [];

  return (
    <>
      <FormField error={workbooksError?.message} label="Workbook">
        <Row gap={2}>
          <Combobox
            isLoading={workbooksLoading}
            options={workbooks ?? []}
            placeholder="Select a workbook…"
            value={query?.workbookId}
            onChange={(workbookId) =>
              onChange({
                ...query,
                workbookId,
              })
            }
          />
          <IconButton
            aria-label="Refresh workbooks"
            icon={RefreshIcon}
            isDisabled={workbooksLoading}
            variant="secondary"
            onClick={() => refetchWorkbooks()}
          />
        </Row>
      </FormField>
      {query?.workbookId && (
        <FormField error={sheetsError?.message} label="Sheet">
          <Row gap={2}>
            <Combobox
              isLoading={sheetsLoading}
              options={sheets}
              placeholder="Select a sheet…"
              value={query?.sheetId}
              onChange={(sheetId) =>
                onChange({
                  ...query,
                  sheetId,
                })
              }
            />
            <IconButton
              aria-label="Refresh sheets"
              icon={RefreshIcon}
              isDisabled={sheetsLoading}
              variant="secondary"
              onClick={() => refetchSheets()}
            />
          </Row>
        </FormField>
      )}
    </>
  );
}

function QueryView(
  props: Readonly<CustomQueryViewProps>,
): ReactElement<any, any> {
  const {
    data: workbooksData,
    isLoading: workbooksLoading,
    error: workbooksError,
  } = useMicrosoftexcelListWorkbooksQuery({
    connectionId: String(props.source.id),
  });
  const workbooks = workbooksData?.microsoftexcelListWorkbooks || [];

  const {
    data: sheetsData,
    isLoading: sheetsLoading,
    error: sheetsError,
  } = useMicrosoftexcelListSheetsQuery(
    {
      connectionId: String(props.source.id),
      workbookId: String(props.query.workbookId),
    },
    { enabled: Boolean(props.query.workbookId) },
  );
  const sheets = sheetsData?.microsoftexcelListSheets || [];

  return (
    <>
      <FormField error={workbooksError?.message} label="Workbook">
        <Combobox
          isDisabled
          isLoading={workbooksLoading}
          options={workbooks}
          value={props.query.workbookId ?? ""}
          onChange={() => null}
        />
      </FormField>
      <FormField label="Sheet" error={sheetsError?.message}>
        <Select
          isDisabled
          isLoading={sheetsLoading}
          options={sheets}
          value={props.query.sheetId ?? ""}
          onChange={() => null}
        />
      </FormField>
    </>
  );
}

const querySchema = Yup.lazy<CustomQuery | undefined>((_) => {
  return Yup.object().shape({
    type: Yup.string().required(),
    workbookId: Yup.string().required(),
    sheetId: Yup.string().required(),
  });
});

export default { QueryForm, QueryView, querySchema };
